export type ResolvedIcon = { url: string | null; vector: boolean };

export interface IconLease {
	icon: Promise<ResolvedIcon>;
	release: () => void;
}

interface IconEntry {
	request: Promise<ResolvedIcon>;
	users: number;
}

export const SVG_MIME = "image/svg+xml";

const SIGNATURE_LENGTH = 512;

const IDLE_LIMIT = 32;

const MISSING: ResolvedIcon = { url: null, vector: false };

const entries = new Map<string, IconEntry>();

export const isVectorIcon = (url?: string | null) => /\.svg([?#].*)?$/i.test(url ?? "");

export const isSvgBlob = async (blob: Blob) => {
	if (blob.type.includes("svg")) return true;
	const head = await blob.slice(0, SIGNATURE_LENGTH).text();
	return /<svg[\s>]/i.test(head);
};

export const asSvgBlob = (blob: Blob) => (blob.type === SVG_MIME ? blob : blob.slice(0, blob.size, SVG_MIME));

const isBlobUrl = (url: string | null): url is string => !!url?.startsWith("blob:");

const download = async (source: string): Promise<Blob | null> => {
	const response = await fetch(source);
	if (!response.ok) return null;

	const blob = await response.blob();
	return blob.size > 0 ? blob : null;
};

const resolve = async (source: string): Promise<ResolvedIcon> => {
	let blob: Blob | null;
	try {
		blob = await download(source);
	} catch {
		return { url: source, vector: isVectorIcon(source) };
	}
	if (!blob) return MISSING;

	const vector = await isSvgBlob(blob);
	return { url: URL.createObjectURL(vector ? asSvgBlob(blob) : blob), vector };
};

const revoke = (entry: IconEntry) => {
	void entry.request.then(({ url }) => {
		if (isBlobUrl(url)) URL.revokeObjectURL(url);
	});
};

const evictIdle = () => {
	const idle = [...entries].filter(([, entry]) => entry.users === 0);
	for (const [source, entry] of idle.slice(0, Math.max(0, idle.length - IDLE_LIMIT))) {
		entries.delete(source);
		revoke(entry);
	}
};

const createEntry = (source: string): IconEntry => {
	const entry: IconEntry = { request: resolve(source), users: 0 };
	void entry.request.then(({ url }) => {
		if (!isBlobUrl(url) && entries.get(source) === entry) entries.delete(source);
	});
	return entry;
};

export const leaseIcon = (source: string): IconLease => {
	const entry = entries.get(source) ?? createEntry(source);
	entries.delete(source);
	entries.set(source, entry);
	entry.users += 1;

	let released = false;
	return {
		icon: entry.request,
		release: () => {
			if (released) return;
			released = true;
			entry.users -= 1;
			if (entry.users === 0) evictIdle();
		},
	};
};

export const resolveIconSource = (source?: string | null): Promise<ResolvedIcon> | null => (source ? leaseIcon(source).icon : null);
